import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemText from '@material-ui/core/ListItemText';
import RemainCountTextField from 'editor/components/FormContent/Field/RemainCountTextField';
import { system900, error } from 'common/components/Mui/styles/color';
import { getOptionLimitCount, OPTIONS_TYPE } from 'common/utils/config';
import { setTextVariantStyle } from 'common/utils/variantStyle';

export default function Dropdown(props) {
    const {
        style,
        optionStyle,
        value,
        native,
        placeholder,
        onSelect = () => {},
        items: options,
        disabled,
        error,
        enableOptionsLimit,
        showRest,
        answerStyle
    } = props;
    const OptionsComponent = native ? 'option' : MenuItem;
    const selectedOption = options.find(o => o.id === value) || {};

    function handleSelect(e) {
        const selectOptionId = Number(e.target.value) || e.target.value;
        const selectOption = options.find(o => o.id === selectOptionId) || {};
        onSelect(selectOptionId, selectOption.label);
    }

    return (
        <FormControl error={!!error} fullWidth css={css(style)}>
            <Select
                value={value}
                onChange={handleSelect}
                disabled={disabled}
                displayEmpty
                native={native}
                MenuProps={{
                    autoFocus: false,
                    disableAutoFocusItem: true,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    disableRestoreFocus: true
                }}
                renderValue={value => {
                    const selectOption = options.find(o => o.id === value) || {};
                    return (
                        <ListItemText
                            primary={selectOption?.label ?? placeholder}
                            primaryTypographyProps={{
                                variant: 'subtitle1',
                                style: { color: answerStyle?.color || system900.color }
                            }}
                            css={dropdownOptionTextStyle(!!error)}
                        />
                    );
                }}
            >
                <OptionsComponent disabled value={''} css={css(optionStyle)}>
                    {placeholder}
                </OptionsComponent>
                {options.map(option => {
                    const isReachLimit =
                        option.type === OPTIONS_TYPE.NORMAL &&
                        enableOptionsLimit &&
                        getOptionLimitCount(option) <= 0;
                    return (
                        <OptionsComponent
                            css={css([optionStyle, isReachLimit && disabledClickStyle])}
                            key={option.id}
                            value={option.id}
                            name={option.label}
                            disabled={isReachLimit}
                        >
                            <ListItemText
                                primary={option.label}
                                primaryTypographyProps={{ variant: 'subtitle1' }}
                                secondary={
                                    enableOptionsLimit && showRest ? (
                                        <RemainCountTextField
                                            option={option}
                                            isEditor={false}
                                            isError={!!option?.error}
                                        />
                                    ) : (
                                        <></>
                                    )
                                }
                                secondaryTypographyProps={{ variant: 'overline' }}
                                css={dropdownOptionTextStyle(!!option?.error)}
                            />
                        </OptionsComponent>
                    );
                })}
            </Select>
            {enableOptionsLimit &&
                showRest &&
                selectedOption?.setting?.submissionLimit !== undefined && (
                    <RemainCountTextField
                        option={selectedOption}
                        isEditor={false}
                        isError={!!error}
                    />
                )}
            {!!error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
    );
}

const disabledClickStyle = css`
    pointer-events: none !important;
    cursor: not-allowed;
`;

const dropdownOptionTextStyle = isError => css`
    &.MuiListItemText-root {
        margin: 0;
    }

    & .MuiListItemText-primary {
        ${setTextVariantStyle('subtitle1')}
        color: ${isError ? error.color : system900.color};
    }

    & .MuiListItemText-secondary {
        ${setTextVariantStyle('overline')}
        color: ${isError ? error.color : system900.color};
    }
`;

Dropdown.propTypes = {
    style: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    optionStyle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    native: PropTypes.bool,
    placeholder: PropTypes.string,
    onSelect: PropTypes.func,
    items: PropTypes.arrayOf(PropTypes.object),
    disabled: PropTypes.bool,
    error: PropTypes.string,
    enableOptionsLimit: PropTypes.bool,
    showRest: PropTypes.bool,
    answerStyle: PropTypes.object
};
